import { PageHeader, Button, Card, CardContent } from "@roberttamas/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Save } from "@/components/icons";
import { TemplateEditor } from "@/components/theme-editor";
import { saveTemplate } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import {
  createEmptyTemplateDraft,
  draftToSaveTemplateReq,
  type TemplateDraft,
} from "@/lib/templates";

export function NewTemplatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<TemplateDraft>(() => createEmptyTemplateDraft());

  const mutation = useMutation({
    mutationFn: () => saveTemplate(draftToSaveTemplateReq(draft)),
    onSuccess: async (resp) => {
      if (resp.warnings.length > 0) {
        toast.warning(t("templates.savedWithWarnings"));
      } else {
        toast.success(t("templates.created"));
      }
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      navigate("/templates");
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const canSave = draft.name.trim().length > 0 && !mutation.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("templates.title")}
        backTo="/templates"
        backLabel={t("templates.backToTemplates")}
        title={t("templates.newTemplate")}
        actions={
          <Button disabled={!canSave} onClick={() => mutation.mutate()}>
            {mutation.isPending ? (
              t("common.saving")
            ) : (
              <>
                <Save className="h-4 w-4" />
                {t("common.save")}
              </>
            )}
          </Button>
        }
      />
      <Card>
        <CardContent className="pt-6">
          <TemplateEditor value={draft} onChange={setDraft} />
        </CardContent>
      </Card>
    </div>
  );
}
